import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { updateGamePlay } from 'api/GameAPI';
import { Emojis, ValueCls } from 'constants/constants';
import { memoize } from 'proxy-memoize';
// import { FbSqUser, getFbSqUser } from 'slices/SqUserSlice';
import { AppThunk, RootState } from 'slices/store';
import {
  QuarterWinner,
  SqGame,
  SqGameProgress,
  SqGameScore,
  SqGameState,
  SqPicksKind,
  SqPlayer,
  SqTeam,
} from 'types/SqGame.types';

export type ActiveGameProps = {
  isLoadingActiveGame: boolean;
} & SqGame;

const initialState: ActiveGameProps = {
  isLoadingActiveGame: false,
  sqGameId: '',
  title: '',
  description: '',
  url: '',
  playerIds: [],
  openSquares: 100,
  homeTeam: {
    id: '',
    name: '',
    logoUrl: '',
    randomNumbers: [],
    pointsScored: 0,
    pointsAllowed: 0,
    displayPosition: 'horizontal',
  },
  awayTeam: {
    id: '',
    name: '',
    logoUrl: '',
    randomNumbers: [],
    pointsScored: 0,
    pointsAllowed: 0,
    displayPosition: 'vertical',
  },
  gameProgress: SqGameProgress.NotStarted,
  gameState: SqGameState.Picks,
  boardLocked: false,
  pickingPlayer: '',
  lastPickingPlayer: '',
  gameAdmin: {
    id: '',
    name: '',
  },
  date: '',
  startingDate: 0,
  quarterScores: [],
  quarterWinners: [],
  bannedPlayerIds: [],
  /** Zero indicates that there is no limit of number of picks per player */
  maxPicksPerPlayer: 0,
  createdBy: '',
  createdOn: 0,
  // FE SqGame exclusive props
  picks: [],
  players: [],
};

// #region ACTIVE GAME ACTIONS ************************************************

// export const doAddPlayerToActiveGame =
//   (player: SqPlayer): AppThunk =>
//   async (dispatch, getStore) => {
//     const activeGame = getActiveSqGame(getStore());
//     if (!activeGame) {
//       console.error(`${Emojis.warning} No game selected`);
//       return;
//     }
//     const marker = getNextUnusedMarker(activeGame.players);
//     const newPlayer: SqPlayer = {
//       ...player,
//       marker,
//       numberOfPicks: 0,
//       gameId: activeGame.sqGameId,
//     };
//     await addPlayerToGameInFB(newPlayer);
//     const playerIds = [...activeGame.playerIds, player.id];
//     const openSquares = activeGame.openSquares - Math.min(10, activeGame.openSquares);
//     const activeGameTemp = { ...activeGame, playerIds, openSquares };
//     updateGamePlay(activeGameTemp);
//   };

export const doUpdateStartingActiveGameTime =
  (time: number): AppThunk =>
  (dispatch, getStore) => {
    const msg = `${Emojis.eyes} %c time ${time} %c, %c date${new Date(time).toISOString()} %c`;
    console.log(msg, ValueCls, '', ValueCls, '');
    const ag = getActiveSqGame(getStore());
    updateGamePlay({ ...ag, startingDate: time });
  };
// #endregion

export const doLockBoard =
  (flag: boolean): AppThunk =>
  (dispatch, getStore) => {
    const activeGame = getActiveSqGame(getStore());
    if (activeGame) {
      updateGamePlay({ ...activeGame, boardLocked: flag });
    }
  };

export const activeGameSlice = createSlice({
  name: 'activegame',
  initialState,
  reducers: {
    setIsLoadingActiveGame: (state, action: PayloadAction<boolean>) => {
      state.isLoadingActiveGame = action.payload;
    },
    setActiveSqGame: (state, action: PayloadAction<SqGame>) => {
      // state = { ...state, ...action.payload };

      state.bannedPlayerIds = [...action.payload.bannedPlayerIds];
      state.boardLocked = action.payload.boardLocked;
      state.createdBy = action.payload.createdBy;
      state.createdOn = action.payload.createdOn;
      state.date = action.payload.date;
      state.description = action.payload.description;
      state.gameAdmin = { ...state.gameAdmin, ...action.payload.gameAdmin };
      state.gameProgress = action.payload.gameProgress;
      state.gameState = action.payload.gameState;
      state.lastPickingPlayer = action.payload.lastPickingPlayer;
      state.maxPicksPerPlayer = action.payload.maxPicksPerPlayer;
      state.openSquares = action.payload.openSquares;
      state.pickingPlayer = action.payload.pickingPlayer;
      state.playerIds = action.payload.playerIds;
      state.quarterScores = [...action.payload.quarterScores];
      state.quarterWinners = [...action.payload.quarterWinners];
      state.sqGameId = action.payload.sqGameId;
      state.matchId = action.payload.matchId ?? '';
      state.startingDate = action.payload.startingDate;
      state.title = action.payload.title;
      state.url = action.payload.url;

      state.awayTeam = { ...state.awayTeam, ...action.payload.awayTeam };
      state.homeTeam = { ...state.homeTeam, ...action.payload.homeTeam };
      state.picks = [...action.payload.picks];
      state.players = [...action.payload.players];
    },
    setGameBoardLockedStatus: (state, action: PayloadAction<boolean>) => {
      state.boardLocked = action.payload;
    },
    setGameStartingDate: (state, action: PayloadAction<number>) => {
      state.startingDate = action.payload;
    },
    setActiveGameState: (state, action: PayloadAction<SqGameState>) => {
      state.gameState = action.payload;
    },
    setActiveGameProgress: (state, action: PayloadAction<SqGameProgress>) => {
      state.gameProgress = action.payload;
    },
    setActiveGamePicks: (state, action: PayloadAction<SqPicksKind>) => {
      state.picks = action.payload;
    },
    setActiveGamePlayers: (state, action: PayloadAction<SqPlayer[]>) => {
      state.players = action.payload;
      state.playerIds = action.payload.map((p) => p.id);
    },
    setActiveGameBannedPlayers: (state, action: PayloadAction<string[]>) => {
      state.bannedPlayerIds = action.payload;
    },
    setActiveGameOpenSquares: (state, action: PayloadAction<number>) => {
      state.openSquares = action.payload;
    },
    setMaxPicksPerPlayer: (state, action: PayloadAction<number>) => {
      state.maxPicksPerPlayer = action.payload;
    },

    // RESET ******************************************************************
    resetActiveGame: () => {
      return { ...initialState };
    },
  },
});

export const {
  setIsLoadingActiveGame,
  // ACTIVE GAME
  setActiveSqGame,
  setGameStartingDate,
  setGameBoardLockedStatus,
  setActiveGameState,
  setActiveGameProgress,
  setActiveGamePicks,
  setActiveGamePlayers,
  setActiveGameBannedPlayers,
  setActiveGameOpenSquares,
  setMaxPicksPerPlayer,
  // RESET
  resetActiveGame,
} = activeGameSlice.actions;

export const getIsLoadingActiveGame = (state: RootState): boolean => state.activegame.isLoadingActiveGame;
// #region GAMES *************************************************************
export const getActiveSqGame = (state: RootState): SqGame => state.activegame;
export const getActiveGameMatchId = (state: RootState): string => state.activegame?.matchId || '';
// export const getActiveSqGameID = (state: RootState): string => state.app.activegame.sqGameId;
export const getIsActiveSqGameBoardLocked = (state: RootState): boolean => state.activegame.boardLocked;
export const getStartingGameDate = (state: RootState): number => state.activegame.startingDate;
export const getActiveSqGamePlayers = (state: RootState): SqPlayer[] => state.activegame.players;
export const getBannedPlayerIds = (state: RootState): string[] => state.activegame.bannedPlayerIds;
export const getIsActiveUserBanned = (state: RootState): boolean =>
  state.activegame.bannedPlayerIds.includes(state.users.user.uid);
export const getActiveGameHomeTeam = (state: RootState): SqTeam => state.activegame.homeTeam;
export const getActiveGameAwayTeam = (state: RootState): SqTeam => state.activegame.awayTeam;
// #endregion

// #region PICK COUNTS ********************************************************
export const getTotalPicksAvailable = (state: RootState): number => {
  const game = getActiveSqGame(state);
  if (!game) return 0;
  let totalPicks = 0;
  game.players.forEach((player) => {
    totalPicks += player.numberOfPicks;
  });
  return 100 - totalPicks;
};
export const getPickedCount = (state: RootState) => {
  const game = getActiveSqGame(state);
  if (!game || game.players.length === 0) return 0;

  return game.players.reduce((acc, player) => acc + (player.picks?.length ?? 0), 0);
};

export const getNumberOfPicksTaken = (state: RootState) => {
  const game = getActiveSqGame(state);
  if (!game || game.players.length === 0) return 0;

  return game.players.reduce((acc, player) => acc + player.numberOfPicks, 0);
};

export const getIsBoardFilled = (state: RootState): boolean => {
  const picksTaken = getNumberOfPicksTaken(state);
  const pickedCount = getPickedCount(state);

  return pickedCount === picksTaken && picksTaken === 100;
};

export const getMaxPicksPerPlayer = (state: RootState) => state.activegame.maxPicksPerPlayer;
// #endregion

// #region UI ****************************************************************
export const getSqActiveGameState = (state: RootState): SqGameState => state.activegame.gameState;
export const getSqActiveGameProgress = (state: RootState): SqGameProgress => state.activegame.gameProgress;
// export const getBoardLocked = (state: RootState): boolean => state.sq.ui.boardLocked;

export const getMarkersUsedByOtherPlayers = memoize((state: RootState): number[] => {
  let usedMarkers: number[] = [];
  const activeGame = getActiveSqGame(state);

  if (!activeGame) return usedMarkers;

  const players = getActiveSqGamePlayers(state).filter((p) => p.id !== state.users.user.uid);
  usedMarkers = players.map((p) => p.marker.markerIndex);

  return usedMarkers;
});

export const getAllGameScores = memoize((state: RootState): SqGameScore[] => {
  const activeGame = getActiveSqGame(state);
  if (!activeGame) return [];
  return activeGame.quarterScores;
});
export const getGameScore = memoize((state: RootState): SqGameScore => {
  const selectedGame = getActiveSqGame(state);
  if (!selectedGame) return { sqGameId: '', home: 0, away: 0, quarter: 0 };

  const scoresLength = selectedGame.quarterScores.length;
  const lastScore: SqGameScore = selectedGame.quarterScores[scoresLength - 1];

  return (
    lastScore ?? {
      sqGameId: selectedGame.sqGameId,
      home: 0,
      away: 0,
      quarter: 0,
    }
  );
});
export const getGameScoreByQuarter = (state: RootState): SqGameScore[] => {
  const activeGame = getActiveSqGame(state);
  if (activeGame) return activeGame.quarterScores;

  return [];
};
export const getQuarterWinners = memoize((state: RootState): QuarterWinner[] => {
  const activeGame = getActiveSqGame(state);
  if (activeGame) return activeGame.quarterWinners;

  return [];
});
// #endregion

// #region USER ***************************************************************
export const isGameAdmin = (state: RootState): boolean => {
  const user = state.users.user;
  const game = getActiveSqGame(state);
  return game && user ? game.gameAdmin.id === user.uid : false;
};
// #endregion

export default activeGameSlice.reducer;
